import { useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import {
  Anchor,
  Button,
  ComboboxItem,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconTriangleFilled,
  IconTriangleInvertedFilled,
} from "@tabler/icons-react";

import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";
import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";
import { truncateWithEllipsis } from "@utils/truncateText";

import { DEFAULT_PAGINATION, QP, ROLES } from "@constants/index";

import { useUpdateAppointment } from "@hooks/mutations/appointment/useUpdateAppointment";
import { useGetAppointments } from "@hooks/queries/appointment/useGetAppointments";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { useAuth } from "@contexts/auth";

import { AppointmentCategory } from "@customTypes/appointment";
import { SortBy } from "@customTypes/index";
import { BasicUser } from "@customTypes/user";

import { TableControls } from "../TableControls";


export interface IncomingAppointmentTableProps {
  searchFilter: string;
  hasAcceptBtn?: boolean;
  variant: "incoming" | "booked";
  category: AppointmentCategory;
  doctors: ComboboxItem[];
  setIsRefreshing: (arg: boolean) => void;
  currentPage: number;
  setCurrentPage: (arg: number) => void;
  doctorFilter?: string;
  setDoctorFilter?: (arg: string) => void;
}

export const TeleconsultationTable = ({
  searchFilter,
  hasAcceptBtn = false,
  variant,
  doctors,
  setIsRefreshing,
  category,
  currentPage,
  setCurrentPage,
  doctorFilter: controlledDoctorFilter,
  setDoctorFilter: setControlledDoctorFilter,
}: IncomingAppointmentTableProps) => {
  const { t, i18n } = useTranslation();
  const { id: userId, roles, permissions, permissionsLoading } = useAuth();
  const { pathname } = useLocation();

  const [internalDoctorFilter, setInternalDoctorFilter] = useState(
    ""
  );
  const doctorFilter = controlledDoctorFilter ?? internalDoctorFilter;
  const assignedOnly = permissions["appointments.view"] === "assigned" ||
    (pathname.includes("queue") && permissions["queue.view"] === "assigned");
  const setDoctorFilter = setControlledDoctorFilter ?? setInternalDoctorFilter;
  const [debounced] = useDebouncedValue(searchFilter, 400);
  const [sortBy, setSortBy] = useState("desc");
  const { columnCount, tableRef } = useTableColumnCount();

  const { data, isError, isSuccess, isLoading, fetchStatus, isRefetching } =
    useGetAppointments({
      isEnabled: !permissionsLoading && Boolean(permissions["appointments.view"]),
      category: "teleconsultation",
      status: variant === "incoming" ? "pending" : "booked",
      pagination: {
        page: currentPage,
        pageSize: DEFAULT_PAGINATION.pageSize,
      },
      search: debounced.toLocaleLowerCase(),
      sortBy: sortBy as SortBy,
      filterBy: assignedOnly ? userId : doctorFilter ?? "",
      isQueued: pathname.includes("queue") ? true : false,
    });
  const { mutate: updateAppointment, isLoading: isUpdateAppointmentLoading } =
    useUpdateAppointment();
  const handleErrors = useQueryErrorHandling();

  const teleconsultationRows = useMemo(() => {
    return data?.newData?.map(item => (
      <Table.Tr key={item.id}>
        <Table.Td>
          {item?.patientId ? (
            <Anchor
              fz={14}
              component={Link}
              to={`/patients/${item.patientId}/profile`}
              c="accent"
              fw={400}>
              {truncateWithEllipsis(
                item.details.firstName + " " + item.details.surname,
                20
              )}
            </Anchor>
          ) : (
            truncateWithEllipsis(
              item.details.firstName + " " + item.details.surname,
              20
            )
          )}
        </Table.Td>
        <Table.Td>
          {item?.priority === "asap" ? (
            <>⏰</>
          ) : (
            <Stack gap={5}>
              <Text fz={14}>
                {formatDateToString(item.appointmentDate as Date)}
              </Text>
              <Text c="dimmed" fz={13}>
                {new Date(item.appointmentDate).toLocaleTimeString(
                  i18n.language,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </Text>
            </Stack>
          )}
        </Table.Td>
        <Table.Td>
          <Stack gap={5}>
            <Text fz={14}>{formatDateToString(item.createdAt as Date)}</Text>
            <Text c="dimmed" fz={13}>
              {item.createdAt.toLocaleTimeString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          {variant === "incoming" ? (
            <Select
              miw={230}
              data={doctors}
              value={(item.doctorAssigned as BasicUser)?.id ?? null}
              onChange={(_val, option) => {
                const id = showLoadingNotification({});
                const jsFormData = new FormData();
                jsFormData.append("doctor_assigned", option.value);
                updateAppointment(
                  {
                    appointmentId: item.id,
                    formData: jsFormData,
                  },
                  {
                    onSuccess: () => {
                      showSuccessNotification({
                        id,
                        title: "success",
                        message: "assign-doctor-success",
                      });
                    },
                    onError: (res: any) => {
                      handleErrors(res, "mutation", id);
                    },
                  }
                );
              }}
            />
          ) : (
            (item.doctorAssigned as BasicUser)?.fullName
          )}
        </Table.Td>
        <Table.Td pos={"relative"}>
          <TableControls
            doctors={doctors}
            variant={variant}
            appointmentType="teleconsultation"
            hasAcceptBtn={hasAcceptBtn}
            row={item}
            isUpdateAppointmentLoading={isUpdateAppointmentLoading}
          />
        </Table.Td>
      </Table.Tr>
    ));
  }, [
    data,
    doctors,
    i18n.language,
    isUpdateAppointmentLoading,
    t,
  ]);

  useEffect(() => {
    if (isRefetching) {
      setIsRefreshing(true);
    } else setIsRefreshing(false);
  }, [isRefetching]);

  return (
    <>
      <Table
        ref={tableRef}
        withRowBorders={true}
        mt={"2rem"}
        miw={1280}
        withTableBorder
        highlightOnHover
        highlightOnHoverColor="var(--clr-gray-100)">
        <Table.Thead>
          <Table.Tr bg={"var(--clr-gray-100)"}>
            <Table.Th>{t("name")}</Table.Th>
            <Table.Th>
              {
                <Button
                  variant="subtle"
                  px={"xs"}
                  color="var(--clr-primary-700)"
                  fz={14}
                  ml={-10}
                  rightSection={
                    sortBy === "desc" ? (
                      <IconTriangleInvertedFilled size={10} />
                    ) : (
                      <IconTriangleFilled size={10} />
                    )
                  }
                  onClick={() => {
                    setSortBy(sortBy === "desc" ? "asc" : "desc");
                    setCurrentPage(1);
                  }}>
                  {t("appointment-date")}
                </Button>
              }
            </Table.Th>
            <Table.Th>{t("created-at")}</Table.Th>
            <Table.Th>
              {!assignedOnly && (
                <Select
                  data={doctors}
                  placeholder={t("doctor")}
                  value={doctorFilter ?? null}
                  onChange={(_val, option) => {
                    setDoctorFilter(option?.value);
                    setCurrentPage(1);
                  }}
                  maw={300}
                  allowDeselect
                  clearable
                />
              )}
            </Table.Th>
            <Table.Th ta={"right"}>
              {isMobile ? t("action") : t("last-updated-action")}
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {renderLoadingErrorData({
            isLoading: isLoading || isRefetching,
            isError: isError,
            isSuccess: isSuccess,
            dataLength: data?.newData?.length || 0,
            emptyDataComponent: (
              <TableEmptyRow
                colSpan={columnCount}
                message={t("empty-appointments")}
              />
            ),
            errorComponent: (
              <TableErrorRow
                colSpan={columnCount}
                message={t("encountered-error")}
              />
            ),
            loadingComponent: (
              <TableSkeletonRows
                columns={columnCount}
                skeletonProps={{
                  widths: ["lg", "md", "md", "lg", "md"],
                  rightAlignLastColumn: true,
                }}
              />
            ),
            dataComponent: teleconsultationRows,
          })}
        </Table.Tbody>
      </Table>
      <TablePagination
        paginationData={data?.pagination || {}}
        paginatedRows={data?.newData?.length || 0}
        onPageChange={setCurrentPage}
        isLoading={isLoading || isRefetching}
      />
    </>
  );
};
